import { makeStyles, tokens } from '@fluentui/react-components';

/**
 * CSS overrides to give Fabric v1 controls a Fluent v2 visual appearance.
 * Apply the `root` class to a container whose descendants include v1 controls.
 *
 * Uses longhand border properties with !important to beat theme-2018
 * specificity (which sets border-color shorthand on .ms-* controls).
 */
export const useV2ControlOverrides = makeStyles({
  root: {
    // ── TextField ──────────────────────────────────────────────
    '& .ms-TextField-fieldGroup': {
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
      minHeight: '32px',

      '&::after': {
        borderTopWidth: '0 !important',
        borderRightWidth: '0 !important',
        borderLeftWidth: '0 !important',
        borderBottomWidth: `${tokens.strokeWidthThick} !important`,
        borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
        borderRadius: `0 0 ${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium}`,
      },
    },
    '& .ms-TextField-fieldGroup:hover': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .ms-TextField .ms-TextField-field': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
    },

    // ── Dropdown ───────────────────────────────────────────────
    '& .ms-Dropdown-title': {
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      lineHeight: '30px',
      minHeight: '32px',
    },
    '& .ms-Dropdown-title:hover': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .ms-Dropdown:focus-within .ms-Dropdown-title': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: tokens.strokeWidthThick,
    },
    '& .ms-Dropdown:focus .ms-Dropdown-title': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: tokens.strokeWidthThick,
    },
    '& .ms-Dropdown.is-open .ms-Dropdown-title': {
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
      borderBottomWidth: tokens.strokeWidthThick,
    },
    // Hide V1 focus :after pseudo-element (draws a second blue border on focus)
    '& .ms-Dropdown:focus::after': {
      borderTopWidth: '0 !important',
      borderRightWidth: '0 !important',
      borderLeftWidth: '0 !important',
      borderBottomWidth: '0 !important',
      outline: 'none !important',
    },
    '& .ms-Dropdown-caretDownWrapper': {
      color: tokens.colorNeutralForeground3,
    },

    // ── Checkbox ───────────────────────────────────────────────
    '& .ms-Checkbox-checkbox': {
      borderRadius: tokens.borderRadiusSmall,
      borderTopColor: tokens.colorNeutralStrokeAccessible,
      borderRightColor: tokens.colorNeutralStrokeAccessible,
      borderBottomColor: tokens.colorNeutralStrokeAccessible,
      borderLeftColor: tokens.colorNeutralStrokeAccessible,
      width: '16px',
      height: '16px',
    },
    '& .ms-Checkbox:hover .ms-Checkbox-checkbox': {
      borderTopColor: tokens.colorCompoundBrandStrokeHover,
      borderRightColor: tokens.colorCompoundBrandStrokeHover,
      borderBottomColor: tokens.colorCompoundBrandStrokeHover,
      borderLeftColor: tokens.colorCompoundBrandStrokeHover,
    },
    '& .ms-Checkbox.is-checked .ms-Checkbox-checkbox': {
      backgroundColor: tokens.colorCompoundBrandBackground,
      borderTopColor: tokens.colorCompoundBrandBackground,
      borderRightColor: tokens.colorCompoundBrandBackground,
      borderBottomColor: tokens.colorCompoundBrandBackground,
      borderLeftColor: tokens.colorCompoundBrandBackground,
    },
    '& .ms-Checkbox.is-checked:hover .ms-Checkbox-checkbox': {
      backgroundColor: tokens.colorCompoundBrandBackgroundHover,
      borderTopColor: tokens.colorCompoundBrandBackgroundHover,
      borderRightColor: tokens.colorCompoundBrandBackgroundHover,
      borderBottomColor: tokens.colorCompoundBrandBackgroundHover,
      borderLeftColor: tokens.colorCompoundBrandBackgroundHover,
    },
    '& .ms-Checkbox-text': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      color: tokens.colorNeutralForeground1,
    },

    // ── Button (default) ──────────────────────────────────────
    '& .ms-Button--default': {
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStroke1} !important`,
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1,
      minHeight: '32px',
    },
    '& .ms-Button--default:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStroke1Hover} !important`,
      color: tokens.colorNeutralForeground1Hover,
    },
    '& .ms-Button--default.is-disabled, & .ms-Button--default:disabled, & .ms-Button--default[aria-disabled="true"]': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      borderTopColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      borderRightColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      borderLeftColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },

    // ── Button (primary) ──────────────────────────────────────
    '& .ms-Button--primary': {
      borderRadius: tokens.borderRadiusMedium,
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      backgroundColor: tokens.colorBrandBackground,
      borderTopColor: `${tokens.colorBrandBackground} !important`,
      borderRightColor: `${tokens.colorBrandBackground} !important`,
      borderLeftColor: `${tokens.colorBrandBackground} !important`,
      borderBottomColor: `${tokens.colorBrandBackground} !important`,
      color: tokens.colorNeutralForegroundOnBrand,
      minHeight: '32px',
    },
    '& .ms-Button--primary:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      borderTopColor: `${tokens.colorBrandBackgroundHover} !important`,
      borderRightColor: `${tokens.colorBrandBackgroundHover} !important`,
      borderLeftColor: `${tokens.colorBrandBackgroundHover} !important`,
      borderBottomColor: `${tokens.colorBrandBackgroundHover} !important`,
    },
    '& .ms-Button--primary.is-disabled, & .ms-Button--primary:disabled, & .ms-Button--primary[aria-disabled="true"]': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      borderTopColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      borderRightColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      borderLeftColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeDisabled} !important`,
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },

    // ── Button (action / command) ─────────────────────────────
    '& .ms-Button--action, & .ms-Button--command': {
      borderRadius: tokens.borderRadiusMedium,
      fontFamily: tokens.fontFamilyBase,
      color: tokens.colorBrandForegroundLink,
    },
    '& .ms-Button--action:hover, & .ms-Button--command:hover': {
      color: tokens.colorBrandForegroundLinkHover,
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    '& .ms-Button--action.is-disabled, & .ms-Button--action:disabled, & .ms-Button--action[aria-disabled="true"], & .ms-Button--command.is-disabled, & .ms-Button--command:disabled, & .ms-Button--command[aria-disabled="true"]': {
      color: tokens.colorNeutralForegroundDisabled,
      backgroundColor: 'transparent',
      cursor: 'not-allowed',
    },

    // ── SearchBox ─────────────────────────────────────────────
    '& .ms-SearchBox': {
      borderRadius: tokens.borderRadiusMedium,
      borderTopWidth: tokens.strokeWidthThin,
      borderRightWidth: tokens.strokeWidthThin,
      borderBottomWidth: tokens.strokeWidthThin,
      borderLeftWidth: tokens.strokeWidthThin,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: `${tokens.colorNeutralStroke1} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,

      '&::after': {
        borderTopWidth: '0 !important',
        borderRightWidth: '0 !important',
        borderLeftWidth: '0 !important',
        borderBottomWidth: `${tokens.strokeWidthThick} !important`,
        borderBottomColor: `${tokens.colorCompoundBrandStroke} !important`,
        borderRadius: `0 0 ${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium}`,
      },
    },
    '& .ms-SearchBox:hover': {
      borderTopColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderRightColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderLeftColor: `${tokens.colorNeutralStroke1Hover} !important`,
      borderBottomColor: `${tokens.colorNeutralStrokeAccessible} !important`,
    },
    '& .ms-SearchBox-icon': {
      color: tokens.colorNeutralForeground3,
    },

    // ── Slider ────────────────────────────────────────────────
    '& .ms-Slider-slideBox .ms-Slider-thumb': {
      borderTopColor: tokens.colorCompoundBrandBackground,
      borderRightColor: tokens.colorCompoundBrandBackground,
      borderBottomColor: tokens.colorCompoundBrandBackground,
      borderLeftColor: tokens.colorCompoundBrandBackground,
      backgroundColor: tokens.colorNeutralBackground1,
    },
    '& .ms-Slider-slideBox .ms-Slider-active': {
      backgroundColor: tokens.colorCompoundBrandBackground,
    },
    '& .ms-Slider-slideBox .ms-Slider-inactive': {
      backgroundColor: tokens.colorNeutralStroke1,
    },

    // ── ChoiceGroup (radio) ───────────────────────────────────
    '& .ms-ChoiceField-field::before': {
      borderTopColor: tokens.colorNeutralStrokeAccessible,
      borderRightColor: tokens.colorNeutralStrokeAccessible,
      borderBottomColor: tokens.colorNeutralStrokeAccessible,
      borderLeftColor: tokens.colorNeutralStrokeAccessible,
    },
    '& .ms-ChoiceField-field.is-checked::before': {
      borderTopColor: tokens.colorCompoundBrandStroke,
      borderRightColor: tokens.colorCompoundBrandStroke,
      borderBottomColor: tokens.colorCompoundBrandStroke,
      borderLeftColor: tokens.colorCompoundBrandStroke,
    },
    '& .ms-ChoiceField-field.is-checked::after': {
      backgroundColor: tokens.colorCompoundBrandBackground,
      borderTopColor: tokens.colorCompoundBrandBackground,
      borderRightColor: tokens.colorCompoundBrandBackground,
      borderBottomColor: tokens.colorCompoundBrandBackground,
      borderLeftColor: tokens.colorCompoundBrandBackground,
    },
    '& .ms-ChoiceField-field:hover::before': {
      borderTopColor: tokens.colorNeutralStrokeAccessibleHover,
      borderRightColor: tokens.colorNeutralStrokeAccessibleHover,
      borderBottomColor: tokens.colorNeutralStrokeAccessibleHover,
      borderLeftColor: tokens.colorNeutralStrokeAccessibleHover,
    },

    // ── Label ─────────────────────────────────────────────────
    '& .ms-Label': {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground1,
      lineHeight: tokens.lineHeightBase300,
    },

    // ── Link ──────────────────────────────────────────────────
    '& .ms-Link': {
      fontFamily: tokens.fontFamilyBase,
      color: tokens.colorBrandForegroundLink,
      fontSize: tokens.fontSizeBase300,
    },
    '& .ms-Link:hover': {
      color: tokens.colorBrandForegroundLinkHover,
    },

    // ── MessageBar ────────────────────────────────────────────
    '& .ms-MessageBar': {
      borderRadius: tokens.borderRadiusMedium,
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
    },
  },
});
